'use client';

import { usePathname, useSearchParams } from 'next/navigation';
import { useState, useEffect, Suspense } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Capacitor } from '@capacitor/core';
import { safeIsStandalonePwa } from '@/lib/pwaRuntimeGuards';
import { useTotalConnectStandaloneWindow } from '@/hooks/useTotalConnectStandaloneWindow';
import { useAuth } from '@/hooks/useAuth';

/**
 * Conditional Layout Wrapper
 * - Shows Header and Footer for all pages EXCEPT public document viewer pages
 * - Header automatically detects white-label mode from client_details (whitelabel_enabled)
 * - White-label clients use their logo_url, primary_color, and secondary_color for branding
 * - Hides Header for Tauri standalone windows (e.g., Total Connect popout)
 */
export default function ConditionalLayout({ children, isRootAppRewrite = false }: { children: React.ReactNode; isRootAppRewrite?: boolean }) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { isAuthenticated } = useAuth();
  const [mounted, setMounted] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isStandaloneWindow, setIsStandaloneWindow] = useState(false);
  const { isStandalone: isTotalConnectStandaloneWindow } = useTotalConnectStandaloneWindow();
  // When a page is loaded with ?embed=1 (e.g. inside the AccountantPortal
  // iframe), suppress the global Header/Footer so the embedder provides all
  // navigation chrome. Without this the iframe shows a doubled header.
  const isEmbedded = searchParams?.get('embed') === '1';
  const shouldHideGlobalChrome = isStandaloneWindow || isTotalConnectStandaloneWindow || isEmbedded;

  // Prevent hydration mismatch by only rendering dynamic content after client-side mount
  useEffect(() => {
    setMounted(true);
    setIsMobile(Capacitor.isNativePlatform() || safeIsStandalonePwa());

    // Check if this is a Tauri standalone window (e.g., Total Connect popout from system tray)
    const checkStandaloneWindow = async () => {
      if (typeof window !== 'undefined' && '__TAURI__' in window) {
        try {
          const { getCurrentWindow } = await import('@tauri-apps/api/window');
          const currentWindow = getCurrentWindow();
          // Standalone windows have labels like 'total-connect', 'messages', etc.
          // Main window has label 'main'
          if (currentWindow.label && currentWindow.label !== 'main') {
            setIsStandaloneWindow(true);
          }
        } catch {
          // Not Tauri or error getting window label
        }
      }
    };
    checkStandaloneWindow();
  }, []);

  // Public document viewer routes that should NOT show header/footer
  // - /secure-link/ - Unified route for all 12 finance document types
  // - /ref-documents/ - Marketing materials and multi-page HTML presentations
  // - /sign/ - Public signature page for QR code signatures
  // - /scan/ - Public asset/equipment scan pages (QR codes)
  // - /enroll/ - Public face enrollment page (has its own AuthFooter)
  // - /biometric/ - Public biometric enrollment/clock-in page (has its own AuthFooter)
  // - /document-upload/ - Public document upload page (tokenized link, standalone)
  // - /vehicle-inspection/ - Public vehicle inspection page (tokenized link)
  // - /field-action/ - Public field-action page (tokenized link)
  // - /forms/ - Public form share pages (tokenized link)
  // - /vote/ - Public vote-by-link pages (tokenized link)
  // - /visit/ - Public visitor booking/register/invite pages
  // - /fleet/ - Public vehicle viewer pages (tokenized link)
  // - /invitation/ - Public invitation acceptance pages
  // - /verify-email - Public guest email verification page
  // - /kiosk - Public kiosk mode pages
  // - /deeplink - Deeplink landing pages (transitional, route themselves into the app)
  // - /auth-blocked - Auth-blocked error page
  // - /call/ - Call/meeting room pages (standalone popup windows)
  // - /meeting/ - Public meeting join pages
  // - /cloud-storage/share/ - Public cloud storage share pages
  // - /shared-documents/ - Public shared document pages
  // - /careers/ - Public tenant careers page (own branding + AuthFooter)
  // - /wi/ - Public work-item share pages (own chrome — often viewed inside
  //   PDF apps' embedded browsers where every pixel of chrome matters)
  // - /connect-adapter/ - Public delegated-OAuth invite pages (own shell)
  const isPublicDocument = pathname?.startsWith('/secure-link/') || pathname?.startsWith('/ref-documents/') || pathname?.startsWith('/sign/') || pathname?.startsWith('/scan/') || pathname?.startsWith('/enroll/') || pathname?.startsWith('/biometric/') || pathname?.startsWith('/document-upload/') || pathname?.startsWith('/vehicle-inspection/') || pathname?.startsWith('/field-action/') || pathname?.startsWith('/forms/') || pathname?.startsWith('/vote/') || pathname?.startsWith('/visit/') || pathname?.startsWith('/fleet/') || pathname?.startsWith('/invitation/') || pathname?.startsWith('/verify-email') || pathname?.startsWith('/kiosk') || pathname?.startsWith('/deeplink') || pathname?.startsWith('/auth-blocked') || pathname?.startsWith('/cloud-storage/share/') || pathname?.startsWith('/shared-documents/') || pathname?.startsWith('/careers/') || pathname?.startsWith('/wi/') || pathname?.startsWith('/connect-adapter/');
  const isCallPage = pathname?.startsWith('/call/') || pathname?.startsWith('/meeting/');
  const isAuthPage = pathname?.startsWith('/auth/');
  const isDiagnosticsPage = pathname?.startsWith('/diagnostics');
  const isRateLimitedPage = pathname === '/rate-limited';
  const isMaintenancePage = pathname === '/maintenance';
  const isDeveloperPortalPage = pathname?.startsWith('/developer-portal');

  // Client-zone routes that need a fixed viewport shell with internal scrolling.
  // Modules/admin-hub already relied on this, and the desktop dashboard/portal must
  // use the same outer contract so UnifiedSideNav can stretch to the available height.
  // "/" only belongs here when the proxy rewrote it to app content (flagged via the
  // x-root-app-rewrite request header) — a logged-in user on a marketing host sees
  // the real public homepage, which must keep natural document scrolling.
  const isClientZoneModule = pathname?.startsWith('/client-zone/modules') || pathname?.startsWith('/client-zone/admin-hub');
  const isClientZoneFixedShell = isClientZoneModule || pathname === '/client-zone/dashboard' || pathname === '/client-zone/portal' || (pathname === '/' && isRootAppRewrite);

  // Client-zone dashboard and other pages use natural document flow.
  // When the middleware rewrites root "/" to "/client-zone/dashboard" (Capacitor apps
  // and authenticated users on dedicated app hosts), usePathname() returns "/".
  // Treat "/" as client-zone for authenticated users so the dashboard layout renders
  // correctly instead of the public marketing page layout.
  const isClientZone = pathname?.startsWith('/client-zone') || (pathname === '/' && (isAuthenticated || isRootAppRewrite));

  // Public marketing pages that should scroll naturally with footer at bottom
  const isPublicPage = !isClientZone && !isPublicDocument && !isCallPage && !isAuthPage && !isRateLimitedPage && !isMaintenancePage;

  // SSR and initial client render: use Suspense to match server output
  // This prevents hydration mismatch since server renders Suspense
  if (!mounted) {
    return (
      <Suspense fallback={<main>{children}</main>}>
        <main>{children}</main>
      </Suspense>
    );
  }

  if (isPublicDocument || isCallPage) {
    // Public document view or call pages - no header/footer
    return <main className="min-h-screen">{children}</main>;
  }

  // Tauri standalone windows (e.g., Total Connect popout from system tray)
  // These should not show the main header/footer - they have their own UI
  if (shouldHideGlobalChrome) {
    return <main className="min-h-screen">{children}</main>;
  }

  if (isAuthPage || isDiagnosticsPage || isRateLimitedPage || isMaintenancePage) {
    // Auth pages, diagnostics and maintenance manage their own standalone shell (no main header/footer)
    return <main className="min-h-screen">{children}</main>;
  }

  // Public marketing pages - natural scroll with footer at bottom (not fixed)
  if (isPublicPage) {
    return (
      <div className="flex flex-col min-h-screen">
        {isMobile && <div className="safe-area-top flex-shrink-0" />}
        <Header />
        <main className="flex-1">{children}</main>
        <Footer />
      </div>
    );
  }

  // Fixed-shell client-zone pages - Header and Footer stay outside the scrolling region,
  // and the route owns internal overflow behavior.
  // On Capacitor/native, use 100dvh (dynamic viewport height) so the shell shrinks
  // when the soft keyboard opens. On older WebView versions (e.g. EMUI) that don't
  // support dvh, the CSS fallback in globals.css maps h-[100dvh] → 100vh.
  if (isClientZoneFixedShell) {
    return (
      <div className="flex flex-col h-[100dvh] overflow-hidden">
        {/* Safe area top bar for Capacitor - fills notch area with primary theme color */}
        {isMobile && <div className="safe-area-top flex-shrink-0" />}
        <Header />
        <main className="flex-1 min-h-0 overflow-hidden">{children}</main>
        <Footer />
      </div>
    );
  }

  // Client-zone dashboard and other non-module pages - natural scroll with footer at bottom
  return (
    <div className="flex flex-col min-h-screen">
      {/* Safe area top bar for Capacitor - fills notch area with primary theme color */}
      {isMobile && <div className="safe-area-top flex-shrink-0" />}
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}
